import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InlineNotification, Loading, Toggle } from '@carbon/react';
import ShiftLabel from 'src/components/Schedule/ShiftLabel';
import { MatchedShift } from 'src/components/Schedule/scheduleModel';
import { timesheetErrorKey } from 'src/components/Timesheet/timesheetFormat';
import { useTenantContext } from 'src/context/useTenantContext';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { KioskRosterRow, useKioskConfirm, useKioskPunch, useKioskRoster } from 'src/hooks/useTimesheets';
import { useAppStore } from 'src/states/app';

export const KIOSK_IDLE_MS = 20_000;
export const KIOSK_DONE_MS = 3_000;

type Step = 'roster' | 'pin' | 'action' | 'done';
type Action = 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END';

const ACTIONS: Record<KioskRosterRow['state'], Action[]> = {
  OUT: ['IN'],
  IN: ['BREAK_START', 'OUT'],
  BREAK: ['BREAK_END', 'OUT'],
};
const ACTION_LABEL: Record<Action, string> = {
  IN: 'timesheet.clockIn',
  OUT: 'timesheet.clockOut',
  BREAK_START: 'timesheet.startBreak',
  BREAK_END: 'timesheet.endBreak',
};
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

/**
 * Shared-tablet clock (spec §7.2). The signed-in device account only opens the page; every
 * action is authorised by the employee's PIN on the server. Nothing here shows pay, and the
 * screen falls back to the name grid — dropping the PIN — after 20 s without a tap, so the
 * next person cannot act as someone who walked away mid-flow.
 */
const TimesheetKiosk = () => {
  const { t } = useTranslation();
  const { isAggregate } = useTenantContext();
  const setFullscreen = useAppStore((state: { setFullscreen: (value: boolean) => void }) => state.setFullscreen);
  const { data: roster, isLoading } = useKioskRoster();
  const punch = useKioskPunch();
  const confirm = useKioskConfirm();
  const restaurant = useCurrentRestaurant();
  const timezone = restaurant?.reportingTimezone || 'Asia/Ho_Chi_Minh';

  const [step, setStep] = useState<Step>('roster');
  const [who, setWho] = useState<KioskRosterRow | null>(null);
  const [pin, setPin] = useState('');
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [matched, setMatched] = useState<MatchedShift | null>(null);

  // Bumped whenever the flow is abandoned (reset) or handed to a new person, so a punch/confirm
  // response that arrives after that point can tell it no longer belongs to the live flow.
  const requestToken = useRef(0);

  const reset = () => {
    requestToken.current += 1;
    setStep('roster');
    setWho(null);
    setPin('');
    setPaid(false);
    setError(null);
    setMatched(null);
  };
  const touch = () => setTick((n) => n + 1);

  useEffect(() => {
    const priorFullscreen = useAppStore.getState().fullscreen;
    setFullscreen(true);
    return () => setFullscreen(priorFullscreen);
  }, [setFullscreen]);

  // Any tap bumps `tick`, restarting the idle clock; on the done screen the shorter timer wins.
  useEffect(() => {
    if (step === 'roster') return undefined;
    const timer = setTimeout(reset, step === 'done' ? KIOSK_DONE_MS : KIOSK_IDLE_MS);
    return () => clearTimeout(timer);
  }, [step, tick]);

  if (isAggregate) {
    return (
      <div className="timesheet-kiosk" data-testid="kiosk-needs-branch">
        <InlineNotification kind="info" lowContrast hideCloseButton title={t('timesheet.kiosk.needsBranch')} />
      </div>
    );
  }
  if (isLoading) return <Loading />;

  const fail = (err: unknown) => {
    setError(t(timesheetErrorKey(err)));
    setPin('');
    setStep('pin');
  };
  const run = (action: Action) => {
    touch();
    const token = requestToken.current;
    const body =
      action === 'BREAK_START'
        ? { employeeId: who?.employeeId as string, pin, action, paid }
        : { employeeId: who?.employeeId as string, pin, action };
    punch.mutate(body, {
      onSuccess: (entry: unknown) => {
        if (token !== requestToken.current) return;
        // A clock-in answers with the planned shift it matched, if any (spec 2026-09-27 §7.3).
        const scheduled = (entry as { scheduledShift?: MatchedShift | null } | undefined)?.scheduledShift ?? null;
        setMatched(action === 'IN' ? scheduled : null);
        setStep('done');
      },
      onError: (err: unknown) => {
        if (token === requestToken.current) fail(err);
      },
    });
  };
  const runConfirm = () => {
    touch();
    const token = requestToken.current;
    confirm.mutate(
      { employeeId: who?.employeeId as string, pin, timesheetId: who?.pendingConfirmationId as string },
      {
        onSuccess: () => {
          if (token === requestToken.current) setStep('done');
        },
        onError: (err: unknown) => {
          if (token === requestToken.current) fail(err);
        },
      }
    );
  };

  return (
    <div className="timesheet-kiosk">
      <h1>{t('timesheet.kiosk.title')}</h1>

      {step === 'roster' && (
        <>
          <p>{t('timesheet.kiosk.choose')}</p>
          <div className="timesheet-kiosk__grid">
            {(roster ?? []).map((row) => (
              <button
                key={row.employeeId}
                type="button"
                className={`timesheet-kiosk__tile timesheet-kiosk__tile--${row.state.toLowerCase()}`}
                data-testid={`kiosk-tile-${row.employeeId}`}
                disabled={!row.pinSet}
                onClick={() => {
                  requestToken.current += 1;
                  setWho(row);
                  setPin('');
                  setError(null);
                  setStep('pin');
                  touch();
                }}
              >
                <strong>{row.displayName}</strong>
                <span>{row.pinSet ? t(`timesheet.kiosk.state.${row.state}`) : t('timesheet.kiosk.askManager')}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 'pin' && who && (
        <div className="timesheet-kiosk__pin">
          <h2>{who.displayName}</h2>
          <p>{t('timesheet.kiosk.enterPin')}</p>
          <div className="timesheet-kiosk__dots" data-testid="kiosk-pin-dots">
            {'•'.repeat(pin.length)}
          </div>
          {error && (
            <p className="timesheet-kiosk__error" data-testid="kiosk-error">
              {error}
            </p>
          )}
          <div className="timesheet-kiosk__keys">
            {KEYS.map((digit) => (
              <button
                key={digit}
                type="button"
                data-testid={`kiosk-key-${digit}`}
                disabled={pin.length >= 6}
                onClick={() => {
                  setPin((value) => value + digit);
                  touch();
                }}
              >
                {digit}
              </button>
            ))}
          </div>
          <Button kind="secondary" onClick={reset}>
            {t('actions.cancel')}
          </Button>
          <Button
            data-testid="kiosk-pin-next"
            disabled={pin.length < 4}
            onClick={() => {
              setError(null);
              setStep('action');
              touch();
            }}
          >
            {t('actions.next')}
          </Button>
        </div>
      )}

      {step === 'action' && who && (
        <div className="timesheet-kiosk__actions">
          <h2>{who.displayName}</h2>
          {ACTIONS[who.state].includes('BREAK_START') && (
            <Toggle
              id="kiosk-paid-break"
              labelText={t('timesheet.paidBreak')}
              toggled={paid}
              onToggle={(checked: boolean) => {
                setPaid(checked);
                touch();
              }}
            />
          )}
          {ACTIONS[who.state].map((action) => (
            <Button
              key={action}
              size="2xl"
              kind={action === 'OUT' ? 'danger' : 'primary'}
              data-testid={`kiosk-action-${action}`}
              disabled={punch.isLoading}
              onClick={() => run(action)}
            >
              {t(ACTION_LABEL[action])}
            </Button>
          ))}
          {who.pendingConfirmationId && (
            <Button
              kind="tertiary"
              size="2xl"
              data-testid="kiosk-confirm"
              disabled={confirm.isLoading}
              onClick={runConfirm}
            >
              {t('timesheet.confirmHours')}
            </Button>
          )}
          <Button kind="ghost" onClick={reset}>
            {t('actions.cancel')}
          </Button>
        </div>
      )}

      {step === 'done' && (
        <div className="timesheet-kiosk__done" data-testid="kiosk-done">
          <h2>{t('timesheet.kiosk.done')}</h2>
          {matched && (
            <p data-testid="kiosk-matched-shift">
              <span>{t('schedule.matchedShift')}: </span>
              <ShiftLabel
                shiftName={matched.shiftName}
                startAt={matched.startAt}
                endAt={matched.endAt}
                timezone={timezone}
              />
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TimesheetKiosk;
